import { Lock, X } from 'lucide-react';
import { useState } from 'react';

import { useBlockActions, useSchedulingBlocks } from '../../hooks/use-scheduling';
import { useToastStore } from '../../stores/toast-store';

import type { SchedulingBlockWire, SchedulingResourceWire } from '../../lib/api-client';
import type { JSX } from 'react';

function fmt(iso: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

/**
 * ResourceBlocksModal — Frente B Sessão 1 (#3). Gestão de DISPONIBILIDADE do operador para
 * UM recurso: fechar período (férias/feriado/almoço → AvailabilityBlock) e reabrir. O motor
 * já subtrai os blocks da disponibilidade ([3.447]); aqui é a escrita. O horário de
 * funcionamento recorrente (WorkingHours) é configuração do tenant → vive no Plano 2.
 */
export function ResourceBlocksModal({
  resource,
  onClose,
}: {
  resource: SchedulingResourceWire;
  onClose: () => void;
}): JSX.Element {
  const blocksQ = useSchedulingBlocks(resource.id);
  const actions = useBlockActions();
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [reason, setReason] = useState('');

  const blocks = blocksQ.data?.data ?? [];
  const busy = actions.create.isPending || actions.remove.isPending;
  const canClose = startsAt !== '' && endsAt !== '' && new Date(endsAt) > new Date(startsAt) && !busy;

  const toast = (variant: 'success' | 'error', description: string): void => {
    useToastStore.getState().push({ variant, title: 'Agenda', description, durationMs: 3500 });
  };

  const onCreate = (): void => {
    actions.create.mutate(
      {
        resourceId: resource.id,
        startsAt: new Date(startsAt).toISOString(),
        endsAt: new Date(endsAt).toISOString(),
        ...(reason.trim().length > 0 && { reason: reason.trim() }),
      },
      {
        onSuccess: () => {
          setStartsAt('');
          setEndsAt('');
          setReason('');
          toast('success', 'Período fechado para este recurso.');
        },
        onError: (err) => toast('error', err instanceof Error ? err.message : 'Falha ao fechar período.'),
      },
    );
  };

  const onRemove = (b: SchedulingBlockWire): void => {
    actions.remove.mutate(
      { id: b.id },
      {
        onSuccess: () => toast('success', 'Período reaberto.'),
        onError: (err) => toast('error', err instanceof Error ? err.message : 'Falha ao reabrir.'),
      },
    );
  };

  return (
    <div
      role="dialog"
      aria-label={`Disponibilidade — ${resource.name}`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-lg rounded-lg p-6 shadow-xl" style={{ background: 'var(--bg-surface)' }}>
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Disponibilidade · {resource.name}</h2>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>
            <X size={14} aria-hidden /> Fechar
          </button>
        </div>
        <p className="mb-4 text-xs" style={{ color: 'var(--fg-3)' }}>
          Feche um período (férias, feriado, almoço) — a disponibilidade some desse intervalo. O
          horário de funcionamento recorrente fica na configuração da agenda.
        </p>

        <div className="mb-3 grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Início</span>
            <input
              type="datetime-local"
              className="w-full rounded border px-2 py-1"
              style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Fim</span>
            <input
              type="datetime-local"
              className="w-full rounded border px-2 py-1"
              style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
            />
          </label>
        </div>
        <label className="mb-3 block">
          <span className="mb-1 block text-sm font-medium">Motivo (opcional)</span>
          <input
            className="w-full rounded border px-2 py-1"
            style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={200}
            placeholder="Ex.: feriado, almoço, congresso"
          />
        </label>
        <button type="button" className="btn btn--primary" onClick={onCreate} disabled={!canClose}>
          <Lock size={14} aria-hidden /> {actions.create.isPending ? 'Fechando…' : 'Fechar período'}
        </button>

        <div
          className="mt-5 mb-2 text-xs font-medium uppercase"
          style={{ color: 'var(--fg-3)', letterSpacing: 'var(--track-caps)' }}
        >
          Períodos fechados
        </div>
        {blocksQ.isLoading && (
          <div className="p-2 text-sm" style={{ color: 'var(--fg-3)' }}>
            Carregando…
          </div>
        )}
        {!blocksQ.isLoading && blocks.length === 0 && (
          <div className="p-2 text-sm" style={{ color: 'var(--fg-3)' }}>
            Nenhum período fechado para este recurso.
          </div>
        )}
        {blocks.length > 0 && (
          <ul className="flex flex-col gap-2">
            {blocks.map((b) => (
              <li
                key={b.id}
                className="flex items-center gap-2 rounded border px-3 py-2 text-sm"
                style={{ borderColor: 'var(--border-default)', background: 'var(--bg-surface-2)' }}
              >
                <span className="font-mono text-xs" style={{ color: 'var(--fg-2)' }}>
                  {fmt(b.startsAt)} – {fmt(b.endsAt)}
                </span>
                {b.reason !== null && (
                  <span className="text-xs" style={{ color: 'var(--fg-3)' }}>
                    {b.reason}
                  </span>
                )}
                <button
                  type="button"
                  className="btn btn--ghost btn--sm ml-auto"
                  disabled={busy}
                  onClick={() => onRemove(b)}
                >
                  Reabrir
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
